import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, RotateCcw, AlertTriangle, FolderKanban, CheckSquare, ListTree, Loader2, ArrowLeft, X } from 'lucide-react';
import { listTrashed, restoreEntity, purgeEntity } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import ConfirmTrashModal from '../../shared/ui/ConfirmTrashModal';

const ITEM_KINDS = {
  project: { label: 'Projet',     badge: 'text-iris-600 bg-iris-50', icon: 'bg-iris-100 text-iris-500', Icon: FolderKanban },
  task:    { label: 'Tâche',      badge: 'text-blue-600 bg-blue-50',     icon: 'bg-blue-100 text-blue-500',     Icon: CheckSquare },
  subtask: { label: 'Sous-tâche', badge: 'text-stone-500 bg-stone-100',  icon: 'bg-stone-100 text-stone-400',   Icon: ListTree },
};

function countTasksForProject(projectId, allTasks) {
  return allTasks.filter(t => t.project_id === projectId).length;
}

function countTaskChildren(taskId, allTasks) {
  const direct = allTasks.filter(t => t.parent_task_id === taskId);
  let count = direct.length;
  for (const child of direct) {
    count += countTaskChildren(child.id, allTasks);
  }
  return count;
}

function TrashItem({ item, itemKind, childCount, onRestore, onPurge, isAdmin, restoring, purging }) {
  const kind = ITEM_KINDS[itemKind] || ITEM_KINDS.task;
  const { Icon } = kind;
  const isProject = itemKind === 'project';
  const deletedDate = new Date(item.deleted_at).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const isBusy = restoring === item.id || purging === item.id;
  const author = item.owner || item.author;
  const childLabel = isProject ? 'tâche' : 'sous-tâche';

  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-stone-100 last:border-b-0 hover:bg-stone-50 transition-colors group">
      <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${kind.icon}`}>
        <Icon size={16} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-stone-800 truncate">{item.name}</span>
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${kind.badge}`}>
            {kind.label}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-stone-400 mt-0.5">
          <span>Supprimé le {deletedDate}</span>
          {author && <span>par {author.name}</span>}
          {childCount > 0 && (
            <span className="text-stone-300">
              · {childCount} {childLabel}{childCount > 1 ? 's' : ''} incluse{childCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onRestore(item)}
          disabled={isBusy}
          className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-40"
          aria-label="Restaurer"
          title="Restaurer"
        >
          {restoring === item.id ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
        </button>

        {isAdmin && (
          <button
            onClick={() => onPurge(item)}
            disabled={isBusy}
            className="p-1.5 rounded-md text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
            aria-label="Supprimer définitivement"
            title="Supprimer définitivement"
          >
            {purging === item.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
          </button>
        )}
      </div>
    </div>
  );
}

export default function ProjectTrashList() {
  const { isAdmin } = useAuth();
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const [purging, setPurging] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const user = isAdmin ? 'all' : 'me';
      const [projectData, taskData] = await Promise.all([
        listTrashed('project', user),
        listTrashed('task', user),
      ]);
      setProjects(projectData);
      setTasks(taskData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const trashedProjectIds = useMemo(
    () => new Set(projects.map(p => p.id)),
    [projects],
  );

  const trashedTaskIds = useMemo(
    () => new Set(tasks.map(t => t.id)),
    [tasks],
  );

  const topLevelTasks = useMemo(() => {
    return tasks.filter(t =>
      !trashedProjectIds.has(t.project_id) &&
      !trashedTaskIds.has(t.parent_task_id),
    );
  }, [tasks, trashedProjectIds, trashedTaskIds]);

  const projectTaskCounts = useMemo(() => {
    const map = new Map();
    for (const p of projects) {
      map.set(p.id, countTasksForProject(p.id, tasks));
    }
    return map;
  }, [projects, tasks]);

  const taskChildCounts = useMemo(() => {
    const map = new Map();
    for (const t of topLevelTasks) {
      map.set(t.id, countTaskChildren(t.id, tasks));
    }
    return map;
  }, [topLevelTasks, tasks]);

  const totalItems = projects.length + topLevelTasks.length;

  const handleRestore = useCallback((item) => {
    const isProject = item.entity_type === 'group';
    const childCount = isProject
      ? (projectTaskCounts.get(item.id) || 0)
      : (taskChildCounts.get(item.id) || 0);
    const label = isProject ? `le projet « ${item.name} »` : `la tâche « ${item.name} »`;
    let desc;
    if (isProject && childCount > 0) {
      desc = `Ce projet et ses ${childCount} tâche${childCount > 1 ? 's' : ''} seront restaurés.`;
    } else if (!isProject && childCount > 0) {
      desc = `Cette tâche et ses ${childCount} sous-tâche${childCount > 1 ? 's' : ''} seront restaurées.`;
    } else {
      desc = `${isProject ? 'Ce projet' : 'Cette tâche'} sera restauré${isProject ? '' : 'e'}.`;
    }

    setConfirmAction({
      type: 'restore',
      item,
      title: `Restaurer ${label} ?`,
      description: desc,
      confirmLabel: 'Restaurer',
    });
  }, [projectTaskCounts, taskChildCounts]);

  const handlePurge = useCallback((item) => {
    const isProject = item.entity_type === 'group';
    const childCount = isProject
      ? (projectTaskCounts.get(item.id) || 0)
      : (taskChildCounts.get(item.id) || 0);
    const label = isProject ? `le projet « ${item.name} »` : `la tâche « ${item.name} »`;
    let desc;
    if (isProject && childCount > 0) {
      desc = `Ce projet et ses ${childCount} tâche${childCount > 1 ? 's' : ''} seront supprimés définitivement. Cette action est irréversible.`;
    } else if (!isProject && childCount > 0) {
      desc = `Cette tâche et ses ${childCount} sous-tâche${childCount > 1 ? 's' : ''} seront supprimées définitivement. Cette action est irréversible.`;
    } else {
      desc = 'Cette action est irréversible. Les données seront perdues.';
    }

    setConfirmAction({
      type: 'purge',
      item,
      title: `Supprimer définitivement ${label} ?`,
      description: desc,
      confirmLabel: 'Supprimer définitivement',
    });
  }, [projectTaskCounts, taskChildCounts]);

  const handleConfirm = useCallback(async () => {
    if (!confirmAction) return;
    const { type, item } = confirmAction;
    const setProgress = type === 'restore' ? setRestoring : setPurging;
    setProgress(item.id);
    setActionError(null);
    try {
      if (type === 'restore') {
        await restoreEntity(item.entity_type, item.bundle, item.id);
      } else {
        await purgeEntity(item.entity_type, item.bundle, item.id);
      }
      setConfirmAction(null);
      await fetchItems();
    } catch (err) {
      console.error(`Trash action failed:`, err);
      setConfirmAction(null);
      setActionError(err.message);
      setTimeout(() => setActionError(null), 6000);
    } finally {
      setProgress(null);
    }
  }, [confirmAction, fetchItems]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <Loader2 size={24} className="animate-spin text-stone-300" />
        <span className="text-sm text-stone-400">Chargement de la corbeille...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3 px-6">
        <AlertTriangle size={24} className="text-amber-500" />
        <p className="text-sm text-stone-500 text-center">{error}</p>
        <button onClick={fetchItems} className="btn btn-ghost">
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      {actionError && (
        <div className="alert alert-danger mb-4 items-center">
          <AlertTriangle size={16} className="flex-shrink-0" />
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className="ml-auto p-0.5 hover:bg-red-100 rounded"><X size={14} /></button>
        </div>
      )}
      <div className="flex items-center gap-3 mb-6">
        <Link to="/projets" className="p-1.5 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors">
          <ArrowLeft size={18} />
        </Link>
        <Trash2 size={20} className="text-stone-400" />
        <h1 className="text-lg font-semibold text-stone-900">Corbeille — Projets & Tâches</h1>
        {totalItems > 0 && (
          <span className="text-xs text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full">
            {totalItems} élément{totalItems > 1 ? 's' : ''}
          </span>
        )}
      </div>

      <p className="text-xs text-stone-400 mb-4">
        Les éléments sont conservés 90 jours avant suppression automatique.
        Restaurer un projet restaure également toutes ses tâches.
      </p>

      {totalItems === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3 px-6">
          <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center">
            <Trash2 size={20} className="text-stone-300" />
          </div>
          <p className="text-sm text-stone-400">La corbeille est vide</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          {projects.length > 0 && (
            <>
              <div className="px-4 py-2 bg-stone-50 text-xs font-medium text-stone-400 uppercase tracking-wider">
                Projets
              </div>
              {projects.map((item) => (
                <TrashItem
                  key={`group-${item.id}`}
                  item={item}
                  itemKind="project"
                  childCount={projectTaskCounts.get(item.id) || 0}
                  onRestore={handleRestore}
                  onPurge={handlePurge}
                  isAdmin={isAdmin}
                  restoring={restoring}
                  purging={purging}
                />
              ))}
            </>
          )}

          {topLevelTasks.length > 0 && (
            <>
              <div className="px-4 py-2 bg-stone-50 text-xs font-medium text-stone-400 uppercase tracking-wider">
                Tâches
              </div>
              {topLevelTasks.map((item) => (
                <TrashItem
                  key={`node-${item.id}`}
                  item={item}
                  itemKind={item.parent_task_id > 0 ? 'subtask' : 'task'}
                  childCount={taskChildCounts.get(item.id) || 0}
                  onRestore={handleRestore}
                  onPurge={handlePurge}
                  isAdmin={isAdmin}
                  restoring={restoring}
                  purging={purging}
                />
              ))}
            </>
          )}
        </div>
      )}

      {confirmAction && (
        <ConfirmTrashModal
          isOpen
          onConfirm={handleConfirm}
          onCancel={() => setConfirmAction(null)}
          title={confirmAction.title}
          description={confirmAction.description}
          confirmLabel={confirmAction.confirmLabel}
          loading={confirmAction.type === 'restore' ? restoring === confirmAction.item.id : purging === confirmAction.item.id}
          variant={confirmAction.type === 'purge' ? 'purge' : 'trash'}
        />
      )}
    </div>
  );
}
